import { useEffect, useRef, useState } from 'react'
import { ApolloClient } from '@apollo/client'

import {
  FinanceFactSummariesDocument,
  FinanceFactSummariesQuery,
  FinancialWorkspaceValueObjectsCostTypeEnum,
  FinancialWorkspaceValueObjectsFinanceTypeEnum,
  FinancialWorkspaceValueObjectsInsightTableTypeEnum,
  InsightTableItemsDocument,
  InsightTableItemsQuery,
} from 'generated/resources/graphql'

import SafeJSON from 'modules/core/lib/SafeJSON'

import { InsightTableGroupBy } from 'modules/finance/components/FinancialWorkspace/types/table'
import { FinancialWorkspaceRecord } from 'modules/finance/components/FinancialWorkspaceDataCollection/types'

import { transformItemToRecord } from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/transformRecords'

interface UseComparisonDataOptions {
  client: ApolloClient<object>
  useFinanceFactSummaries: boolean
  groupBy: InsightTableGroupBy
  search?: string
  effectiveCostType: FinancialWorkspaceValueObjectsCostTypeEnum
  formattedCompareToStartDate?: string
  formattedCompareToEndDate?: string
  hasComparisonDate: boolean
  // Serialized filter keys for stable dependency tracking
  legalEntityIdsKey: string
  locationIdsKey: string
  costCenterIdsKey: string
  employeeIdsKey: string
  conceptIdsKey: string
  financeTypesKey: string
}

/**
 * Hook to fetch comparison period data separately when comparison mode is active.
 * Returns a ref to the comparison data map and a loading flag.
 */
export const useComparisonData = ({
  client,
  useFinanceFactSummaries,
  groupBy,
  search,
  effectiveCostType,
  formattedCompareToStartDate,
  formattedCompareToEndDate,
  hasComparisonDate,
  legalEntityIdsKey,
  locationIdsKey,
  costCenterIdsKey,
  employeeIdsKey,
  conceptIdsKey,
  financeTypesKey,
}: UseComparisonDataOptions) => {
  const comparisonDataRef = useRef<Map<string | number, FinancialWorkspaceRecord>>(new Map())
  const [isLoadingComparison, setIsLoadingComparison] = useState(false)

  useEffect(() => {
    if (!hasComparisonDate) {
      comparisonDataRef.current.clear()
      return
    }

    const fetchComparison = async () => {
      setIsLoadingComparison(true)
      const comparisonMap = new Map<string | number, FinancialWorkspaceRecord>()

      try {
        const legalEntityIds = SafeJSON.parse<number[]>(legalEntityIdsKey, [])
        const locationIds = SafeJSON.parse<number[]>(locationIdsKey, [])
        const costCenterIds = SafeJSON.parse<number[]>(costCenterIdsKey, [])
        const employeeIds = SafeJSON.parse<number[]>(employeeIdsKey, [])
        const conceptIds = SafeJSON.parse<number[]>(conceptIdsKey, [])
        const financeTypesArray = SafeJSON.parse<FinancialWorkspaceValueObjectsFinanceTypeEnum[]>(
          financeTypesKey,
          []
        )

        if (useFinanceFactSummaries) {
          const result = await client.query<FinanceFactSummariesQuery>({
            query: FinanceFactSummariesDocument,
            variables: {
              formattedStartDate: formattedCompareToStartDate,
              formattedEndDate: formattedCompareToEndDate,
              groupBy,
              legalEntityIds,
              locationIds,
              costCenterIds,
              employeeIds,
              conceptIds,
              search: search ?? undefined,
              financeTypes: financeTypesArray,
              customCategoryIds: [],
              managerIds: [],
              excludeChildrenData: groupBy === InsightTableGroupBy.Total,
              costType: effectiveCostType,
              itemsPerPage: 1000,
            },
            fetchPolicy: 'network-only',
          })

          const edges = result.data.financialWorkspace.financeFactSummariesConnection.edges
          edges.forEach((edge) => {
            const record = transformItemToRecord({
              id: edge.node.id,
              label: edge.node.label,
              rows: edge.node.rows,
              children: edge.node.children,
              categoryLabel: edge.node.categoryLabel,
              category: edge.node.category,
              active: edge.node.active ?? undefined,
              imageUrl: edge.node.imageUrl,
              hasChildren: edge.node.hasChildren,
            })
            comparisonMap.set(record.id, record)
          })
        } else {
          const insightTableType: FinancialWorkspaceValueObjectsInsightTableTypeEnum = 'payroll'
          const result = await client.query<InsightTableItemsQuery>({
            query: InsightTableItemsDocument,
            variables: {
              resourceFilter: {
                formattedStartDate: formattedCompareToStartDate,
                formattedEndDate: formattedCompareToEndDate,
                groupBy,
                legalEntityIds,
                locationIds,
                costCenterIds,
                employeeIds,
                conceptIds,
                search: search ?? undefined,
                insightTableType,
              },
              itemsPerPage: 1000,
            },
            fetchPolicy: 'network-only',
          })

          const edges = result.data.financialWorkspace.insightTableItemsConnection.edges
          edges.forEach((edge) => {
            const record = transformItemToRecord({
              id: edge.node.id,
              label: edge.node.label,
              rows: edge.node.rows,
              children: edge.node.children,
              categoryLabel: edge.node.categoryLabel,
              category: edge.node.category,
              active: edge.node.active ?? undefined,
              imageUrl: edge.node.imageUrl,
            })
            comparisonMap.set(record.id, record)
          })
        }

        comparisonDataRef.current = comparisonMap
      } catch (error) {
        // Failed to fetch comparison data - clear the ref and continue
        comparisonDataRef.current.clear()
      } finally {
        setIsLoadingComparison(false)
      }
    }

    fetchComparison()
  }, [
    hasComparisonDate,
    formattedCompareToStartDate,
    formattedCompareToEndDate,
    groupBy,
    legalEntityIdsKey,
    locationIdsKey,
    costCenterIdsKey,
    employeeIdsKey,
    conceptIdsKey,
    financeTypesKey,
    search,
    useFinanceFactSummaries,
    effectiveCostType,
    client,
  ])

  return { comparisonDataRef, isLoadingComparison }
}
